import { Container, Graphics } from 'pixi.js';
import gsap from 'gsap';

const GOLD = 0xf4c724;
const WHITE = 0xffffff;
const CYAN = 0x22d3ee;

/** Expanding shockwave ring — used at the moment of impact. */
export function spawnShockwave(
  parent: Container,
  x: number,
  y: number,
  color = GOLD,
  maxR = 120,
): void {
  const g = new Graphics();
  g.x = x;
  g.y = y;
  parent.addChild(g);
  const st = { r: 8, a: 0.95, w: 6 };
  gsap.to(st, {
    r: maxR,
    a: 0,
    w: 1,
    duration: 0.7,
    ease: 'power2.out',
    onUpdate: () => {
      if (g.destroyed) return;
      g.clear().circle(0, 0, st.r).stroke({ width: st.w, color, alpha: st.a });
    },
    onComplete: () => g.destroy(),
  });
}

/** Confetti shards spraying upward + outward, then tumbling down. */
export function spawnConfetti(
  parent: Container,
  x: number,
  y: number,
  count = 28,
): void {
  const palette = [GOLD, WHITE, CYAN, 0xe63946, 0x9b5de5, 0x3b82f6];
  for (let i = 0; i < count; i++) {
    const shard = new Graphics();
    const col = palette[Math.floor(Math.random() * palette.length)]!;
    const w = 4 + Math.random() * 5;
    const h = 7 + Math.random() * 7;
    shard.rect(-w / 2, -h / 2, w, h).fill({ color: col });
    shard.x = x;
    shard.y = y;
    shard.rotation = Math.random() * Math.PI;
    parent.addChild(shard);

    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
    const speed = 90 + Math.random() * 160;
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;
    const dur = 0.9 + Math.random() * 0.7;

    gsap.to(shard, {
      x: x + vx,
      y: y + vy + 220, // gravity pulls them down past the apex
      rotation: shard.rotation + (Math.random() - 0.5) * 12,
      duration: dur,
      ease: 'power1.out',
    });
    gsap.to(shard, {
      alpha: 0,
      duration: 0.4,
      delay: dur - 0.4,
      onComplete: () => shard.destroy(),
    });
  }
}

/** Radiating light beams (sunburst) behind the winner — rotates + fades. */
export function spawnSunburst(parent: Container, x: number, y: number): void {
  const g = new Graphics();
  const beams = 12;
  for (let i = 0; i < beams; i++) {
    const a = (i / beams) * Math.PI * 2;
    const x2 = Math.cos(a) * 200;
    const y2 = Math.sin(a) * 200;
    const perp = a + Math.PI / 2;
    const wx = Math.cos(perp) * 16;
    const wy = Math.sin(perp) * 16;
    g.moveTo(0, 0)
      .lineTo(x2 - wx, y2 - wy)
      .lineTo(x2 + wx, y2 + wy)
      .closePath()
      .fill({ color: GOLD, alpha: 0.18 });
  }
  g.x = x;
  g.y = y;
  g.scale.set(0.3);
  parent.addChildAt(g, 0);
  gsap.to(g.scale, { x: 1.4, y: 1.4, duration: 1.0, ease: 'power2.out' });
  gsap.to(g, { rotation: 0.6, duration: 1.4, ease: 'none' });
  gsap.to(g, { alpha: 0, duration: 0.8, delay: 0.6, onComplete: () => g.destroy() });
}

/** Heavy "STOMP" impact dust + crack lines at the loser's position. */
export function spawnStompImpact(parent: Container, x: number, y: number): void {
  // Dust cloud — several expanding grey puffs.
  for (let i = 0; i < 7; i++) {
    const puff = new Graphics();
    const r = 8 + Math.random() * 8;
    puff.circle(0, 0, r).fill({ color: 0xcdd2e0, alpha: 0.5 });
    const dx = (Math.random() - 0.5) * 80;
    puff.x = x + dx;
    puff.y = y;
    puff.scale.set(0.4);
    parent.addChild(puff);
    gsap.to(puff.scale, { x: 1.8, y: 1.8, duration: 0.5, ease: 'power2.out' });
    gsap.to(puff, {
      y: y - 10 - Math.random() * 18,
      alpha: 0,
      duration: 0.55,
      ease: 'power1.out',
      onComplete: () => puff.destroy(),
    });
  }
  // Impact crack lines radiating out along the ground.
  const crack = new Graphics();
  crack.x = x;
  crack.y = y;
  parent.addChild(crack);
  for (let i = 0; i < 6; i++) {
    const a = -0.2 + (i / 5) * (Math.PI + 0.4);
    const len = 26 + Math.random() * 22;
    crack.moveTo(0, 0).lineTo(Math.cos(a) * len, Math.sin(a) * len * 0.4)
      .stroke({ width: 2.5, color: WHITE, alpha: 0.9 });
  }
  gsap.to(crack, { alpha: 0, duration: 0.6, ease: 'power2.out', onComplete: () => crack.destroy() });
  // White flash at impact point.
  spawnShockwave(parent, x, y, WHITE, 70);
}

/** Big "WIN" star pop at the bell. */
export function spawnStarPop(parent: Container, x: number, y: number): void {
  const star = new Graphics();
  const spikes = 5;
  const outer = 34;
  const inner = 15;
  let rot = -Math.PI / 2;
  const step = Math.PI / spikes;
  star.moveTo(Math.cos(rot) * outer, Math.sin(rot) * outer);
  for (let i = 0; i < spikes; i++) {
    rot += step;
    star.lineTo(Math.cos(rot) * inner, Math.sin(rot) * inner);
    rot += step;
    star.lineTo(Math.cos(rot) * outer, Math.sin(rot) * outer);
  }
  star.closePath().fill({ color: GOLD }).stroke({ width: 2, color: WHITE });
  star.x = x;
  star.y = y;
  star.scale.set(0);
  parent.addChild(star);
  const tl = gsap.timeline({ onComplete: () => star.destroy() });
  tl.to(star.scale, { x: 1.4, y: 1.4, duration: 0.2, ease: 'back.out(3)' });
  tl.to(star, { rotation: 0.5, duration: 0.6, ease: 'none' }, 0);
  tl.to(star.scale, { x: 0.9, y: 0.9, duration: 0.5, ease: 'sine.inOut' });
  tl.to(star, { alpha: 0, y: y - 30, duration: 0.4 }, '-=0.2');
}
